import { Storage } from '.';
import { TLoginResponse } from '/src/api';
import { User } from '/src/lib/models';
import { StorageKeys, TSessionToken } from '/src/lib/types';

/**
 * Session Token
 */
export const getSessionToken = (): TSessionToken | undefined => {
  const token = Storage.get<unknown>(StorageKeys.SessionToken);
  if (!token || typeof token !== 'object') return undefined;
  const sessionToken = token as Partial<TSessionToken>;
  return typeof sessionToken.token === 'string' ? (sessionToken as TSessionToken) : undefined;
};

export const setSessionToken = (token: TSessionToken) => {
  Storage.set(StorageKeys.SessionToken, token);
};

export const removeSessionToken = () => {
  Storage.remove(StorageKeys.SessionToken);
};

/**
 * Session User
 */
export const getSessionUser = (): User | undefined => {
  const user = Storage.get<unknown>(StorageKeys.SessionUser);
  if (!user || typeof user !== 'object') return undefined;
  const sessionUser = user as Partial<User>;
  return typeof sessionUser.id === 'number' && !!sessionUser.role?.id ? (sessionUser as User) : undefined;
};

export const setSessionUser = (user: User) => {
  Storage.set(StorageKeys.SessionUser, user);
};

export const removeSessionUser = () => {
  Storage.remove(StorageKeys.SessionUser);
};

/**
 * Session Expiration
 */
export const SESSION_TTL_MS = 30 * 60 * 1000;
export const SESSION_EXPIRED_REASON = 'sessionExpired';

export const setSessionTtl = (ttlMs: number) => {
  Storage.set(StorageKeys.SessionTtl, ttlMs);
};

const getSessionTtl = (): number => {
  const ttl = Storage.get<number>(StorageKeys.SessionTtl);
  return typeof ttl === 'number' && ttl > 0 ? ttl : SESSION_TTL_MS;
};

export const removeSessionTtl = () => {
  Storage.remove(StorageKeys.SessionTtl);
};

export const getSessionExpiration = (): number | undefined => {
  const value = Storage.get<number>(StorageKeys.SessionExpiration);
  return typeof value === 'number' ? value : undefined;
};

export const setSessionExpiration = (expiresAt: number) => {
  Storage.set(StorageKeys.SessionExpiration, expiresAt);
};

export const removeSessionExpiration = () => {
  Storage.remove(StorageKeys.SessionExpiration);
};

export const refreshSessionExpiration = () => {
  setSessionExpiration(Date.now() + getSessionTtl());
};

export const isSessionExpired = (): boolean => {
  const expiresAt = getSessionExpiration();
  return expiresAt !== undefined && Date.now() >= expiresAt;
};

/**
 * Session Login Data
 */
export const getSession = () => {
  return Storage.get(StorageKeys.Session);
};

export const setSession = (loginData: TLoginResponse) => {
  Storage.set(StorageKeys.Session, loginData);
};

export const removeSession = () => {
  Storage.remove(StorageKeys.Session);
};
